// Global styles can live here
import './styles.css';
import 'react-toastify/dist/ReactToastify.css';

import React from 'react';
import Head from 'next/head';
import { theme } from 'GlobalStyles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ThemeProvider } from '@mui/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import { StylesProvider } from '@mui/styles';
import * as Sentry from '@sentry/nextjs';
import { generateQueryClient } from 'config/reactQuery/queryClient';
import { QueryClientProvider, Hydrate } from 'react-query';
import { dayjsInitializer, QueryContextProvider } from '@alluradirect/react';
import { handleOnErrorToast, handleOnSuccessToast } from 'helpers/toastHelpers';
import { customQuery } from 'config/reactQuery/reactQuery';
import { StyledToastContainer } from 'styles';
import { Slide } from 'react-toastify';
import { useGoogleTagManager } from 'webHooks/useGoogleTagManager';
import { Provider } from 'react-redux';
import { store } from 'store/appStore';
import Script from 'next/script';

// Required to make the dayjs instance in this application work with the shared application
dayjsInitializer();

function MyApp({ Component, pageProps, emotionCache }: any) {
  // https://react-query.tanstack.com/guides/ssr#using-hydration
  const [queryClient] = React.useState(() => generateQueryClient());
  useGoogleTagManager();

  return (
    <Sentry.ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <QueryContextProvider
            isOffline={false}
            // By default useAppMutation and useAppQuery hooks will show toasts
            // This can be disabled per hook
            onErrorToast={handleOnErrorToast}
            onSuccessToast={handleOnSuccessToast}
            queryFn={customQuery}
          >
            <CacheProvider value={emotionCache}>
              <Head>
                <title>alluraDirect</title>
                <meta
                  name="viewport"
                  content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no"
                />
              </Head>

              <Script
                src={
                  'https://maps.googleapis.com/maps/api/js?key=AIzaSyBWM3cDz-C4uU-WlG3K1jjdLkaFbLPtzek&libraries=places'
                }
                strategy="afterInteractive"
              />

              <StyledEngineProvider injectFirst>
                <StylesProvider injectFirst>
                  <ThemeProvider theme={theme}>
                    <StyledThemeProvider theme={theme}>
                      <CssBaseline />
                      <Provider store={store}>
                        <Component {...pageProps} />
                      </Provider>
                      <StyledToastContainer
                        position="top-right"
                        autoClose={3000}
                        limit={3}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        transition={Slide}
                        draggable
                        pauseOnFocusLoss={false}
                      />
                    </StyledThemeProvider>
                  </ThemeProvider>
                </StylesProvider>
              </StyledEngineProvider>
            </CacheProvider>
          </QueryContextProvider>
        </Hydrate>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default MyApp;
